import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";

import type { IEmbedEvent } from "~/feature/embed/types";
import type { IEmbedSize } from "~/feature/embed/utils/getEmbedMinSize";
import type { IMarket } from "~/types/event";

import type { EmbedOptionEnum, EmbedOptionsState } from "../types";
import { createEmbedState, embedReducer } from "./embedReducer";

interface IEmbedProviderProps {
  event: IEmbedEvent;
  markets: IMarket[];
  defaultSelection: string[];
}

interface IEmbedContextType {
  event: IEmbedEvent;
  markets: IMarket[];
  selections: string[];
  options: EmbedOptionsState;
  size: IEmbedSize | null;
  toggleMarket: (marketId: string) => void;
  selectConsensus: (marketId: string) => void;
  setOption: (option: EmbedOptionEnum, value: boolean) => void;
  setSize: (size: IEmbedSize) => void;
}

const EmbedContext = createContext<IEmbedContextType | null>(null);

export const EmbedProvider = ({
  event,
  markets,
  defaultSelection,
  children,
}: PropsWithChildren<IEmbedProviderProps>) => {
  const [state, dispatch] = useReducer(
    embedReducer,
    defaultSelection,
    createEmbedState
  );

  const toggleMarket = useCallback(
    (marketId: string) => dispatch({ type: "toggleMarket", marketId, markets }),
    [markets]
  );

  const selectConsensus = useCallback(
    (marketId: string) =>
      dispatch({ type: "selectConsensus", marketId, markets }),
    [markets]
  );

  const setOption = useCallback(
    (option: EmbedOptionEnum, value: boolean) =>
      dispatch({ type: "setOption", option, value }),
    []
  );

  const setSize = useCallback(
    (size: IEmbedSize) => dispatch({ type: "setSize", size }),
    []
  );

  const value = useMemo(
    () => ({
      ...state,
      event,
      markets,
      toggleMarket,
      selectConsensus,
      setOption,
      setSize,
    }),
    [state, event, markets, toggleMarket, selectConsensus, setOption, setSize]
  );

  return (
    <EmbedContext.Provider value={value}>{children}</EmbedContext.Provider>
  );
};

export const useEmbed = (): IEmbedContextType => {
  const context = useContext(EmbedContext);

  if (!context) {
    throw new Error("useEmbed must be used within an EmbedProvider");
  }

  return context;
};
